/**
 * Universidad práctica — curriculum por niveles, evidencia de taller, sin XP.
 */

import {
  listProjects,
  PILOT_WIRING_PROJECT_ID,
  type EngineeringProject,
} from "@/lib/prometheusProjectsStore";
import { listOnlineDevices } from "@/lib/deviceRegistryStore";

const QUIZ_KEY = "prometheus_university_quiz_v1";
const MODULE_QUIZ_KEY = "prometheus_university_module_quiz_v1";

export type UniversityTrackId = "mech" | "elec" | "energy" | "ai";
export type CurriculumLevel = "fundamentos" | "aplicado" | "especialidad";
export type ArtifactPracticeTab = "despiece" | "wiring" | "resumen" | "bitacora";
export type PracticeKind = "wiring" | "despiece" | "steps" | "monitor" | null;

export type UniversityQuiz = {
  question: string;
  options: string[];
  /** 0-based correct index */
  correctIndex: number;
  explain: string;
};

export type UniversityModule = {
  id: string;
  title: string;
  detail: string;
  /** Short lesson body shown in-panel (real teaching, not fluff). */
  lesson: string;
  level: CurriculumLevel;
  /** Prior module ids that should be understood first (soft unlock). */
  requires?: string[];
  /** Workshop practice expected before marking module strong. */
  practice?: PracticeKind;
  quiz: UniversityQuiz;
  chatSkill?: string;
  /** Extra prompt fragment when studying this module in chat. */
  chatFocus?: string;
  artifactTab?: ArtifactPracticeTab;
};

export type UniversityTrack = {
  id: UniversityTrackId;
  title: string;
  blurb: string;
  modules: UniversityModule[];
  /** Keywords matched against project title/discipline */
  projectHints: string[];
  chatSkill?: string;
  /** Optional safety focus line for elec/energy */
  safetyNote?: string;
};

export const LEVEL_LABEL: Record<CurriculumLevel, string> = {
  fundamentos: "Fundamentos",
  aplicado: "Aplicado",
  especialidad: "Especialidad",
};

export const LEVEL_ORDER: CurriculumLevel[] = [
  "fundamentos",
  "aplicado",
  "especialidad",
];

export const UNIVERSITY_TRACKS: UniversityTrack[] = [
  {
    id: "mech",
    title: "Mecánica & Diseño",
    blurb: "Estática, FoS, materiales, despiece y apps (Fusion / FreeCAD).",
    projectHints: ["gabinete", "enclosure", "ip67", "carcasa", "mecan", "engranaje", "petg"],
    chatSkill: "@skill:despiece",
    modules: [
      {
        id: "mech-forces",
        title: "Fuerzas & torque en el taller",
        detail: "Momento, brazo y FoS con piezas que sí imprimes o mecanizas.",
        level: "fundamentos",
        lesson: `Torque τ = F · d. Si aprietas un tornillo M3 con un brazo corto, la fuerza en la rosca sube rápido: por eso se rompen cabezas Allen en PETG.
Factor de seguridad FoS = σ_fluencia / σ_trabajo. En taller maker apunta FoS ≥ 2.0 en piezas de carga; ≥ 3 si hay impacto o vibración (impresora, drone, puerta).
Práctica: mira una pieza del despiece, estima la fuerza y el brazo, y decide si el material aguanta o hay que subir perímetros / metal.`,
        quiz: {
          question: "Si duplicas el brazo de palanca con la misma fuerza, el torque…",
          options: ["Se reduce a la mitad", "Se duplica", "No cambia", "Solo cambia si hay WiFi"],
          correctIndex: 1,
          explain: "τ = F · d — más brazo, más momento. Por eso las llaves largas aprietan más con menos esfuerzo.",
        },
        chatSkill: "@skill:physics",
        chatFocus: "Explícame torque y FoS con un ejemplo del gabinete PETG.",
        artifactTab: "despiece",
        practice: "despiece",
      },
      {
        id: "mech-despiece",
        title: "Despiece y tolerancias",
        detail: "Explosión, holgura 0.2 mm y secuencia de montaje.",
        level: "aplicado",
        requires: ["mech-forces"],
        lesson: `Despiece = orden de montaje + holguras. En PETG/ASA para PR, holgura ~0.20 mm entre carcasa y tapa evita forzar y deja sitio a junta TPU.
Secuencia típica: bancada → electrónica → junta → tapa → prensaestopas. Si cierras la tapa antes del cableado, retrabajas.
DFMA: menos tornillos distintos, acceso a fusible/USB sin desarmar todo, countersunk al ras para IP67.`,
        quiz: {
          question: "Holgura típica DFMA en gabinete PETG IP67 para encajar sin lijar:",
          options: ["0 mm", "~0.20 mm", "2 mm", "Solo el color del filamento"],
          correctIndex: 1,
          explain: "~0.2 mm es práctica en clima húmedo PR: encaje limpio + espacio para junta.",
        },
        chatSkill: "@skill:despiece",
        chatFocus: "Guíame el despiece del proyecto activo paso a paso.",
        artifactTab: "despiece",
        practice: "despiece",
      },
      {
        id: "mech-dfma",
        title: "DFMA para impresión 3D PR",
        detail: "PETG/ASA, humedad y fijación inox/galvanizado anti-salitre.",
        level: "especialidad",
        requires: ["mech-despiece"],
        lesson: `Filamento en PR: seca PETG/nylon; humedad hincha capas y mata tolerancia.
Hardware: Inox 316 o galvanizado — el salitre come zinc barato y acero negro.
Orientación de impresión: evita layer lines en dirección de flexión crítica; nervaduras al cubo en inercia (I).`,
        quiz: {
          question: "En costa PR, ¿qué fijación priorizas en un gabinete exterior?",
          options: ["Tornillos negros de drywall", "Inox 316 o galvanizado", "Solo adhesivo CA", "Madera sin tratar"],
          correctIndex: 1,
          explain: "Salitre + humedad: Inox 316 o galvanizado. El hardware barato se oxida y pierde preload.",
        },
        chatSkill: "@skill:asce7",
        chatFocus: "Materiales y fijación anti-salitre para mi carcasa en PR.",
        artifactTab: "despiece",
        practice: "steps",
      },
    ],
  },
  {
    id: "elec",
    title: "Electrónica & MCU",
    blurb: "ESP32 / Arduino, buses, potencia limpia, conformal coating.",
    projectHints: ["esp32", "arduino", "wiring", "relay", "i2c", "mcu", "gabinete"],
    chatSkill: "@skill:wiring",
    safetyNote:
      "Checklist: GND común · fusible en +V · nunca 12V a GPIO · conformal si hay salitre.",
    modules: [
      {
        id: "elec-pins",
        title: "Pines y alimentaciones reales",
        detail: "3.3V lógica vs 5V/12V potencia.",
        level: "fundamentos",
        lesson: `ESP32: GPIO = 3.3V. VIN/USB pueden ser 5V. Motores/relays a 12–24V viven en otro dominio.
Regla: señal lógica al IN del módulo relay; bobina/carga al lado de potencia; GND común entre MCU y módulo.
Si metes 12V a un GPIO, mueres el chip — no es “si el cable es corto”.`,
        quiz: {
          question: "¿Por qué el GPIO del ESP32 no lleva 12V del relay?",
          options: [
            "Por el USB",
            "GPIO es 3.3V; 12V lo destruye — usa módulo + GND común",
            "Solo falla sin WiFi",
            "Es seguro si el cable es corto",
          ],
          correctIndex: 1,
          explain: "Lógica 3.3V vs potencia 12V. El módulo relay es el puente; GND común obligatorio.",
        },
        chatSkill: "@skill:wiring",
        chatFocus: "Revisa pines y alimentaciones del wiring del proyecto.",
        artifactTab: "wiring",
        practice: "wiring",
      },
      {
        id: "elec-bus",
        title: "I2C / SPI / UART",
        detail: "Pull-ups, baud y tierra común.",
        level: "aplicado",
        requires: ["elec-pins"],
        lesson: `I2C: SDA/SCL con pull-up a 3.3V (típico 4.7k). Sin pull-up, el bus flota y “a veces funciona”.
UART: mismo baud (115200 en el piloto), GND común, TX↔RX cruzado.
SPI: CLK/MOSI/MISO/CS — más cables, más rápido; sigue siendo 3.3V en ESP32.`,
        quiz: {
          question: "En I2C a 3.3V sin pull-ups externos en un sensor lejano, lo más probable es…",
          options: [
            "Más velocidad automática",
            "Bus inestable o sin ACK",
            "Que el relay se active solo",
            "Que el FoS baje",
          ],
          correctIndex: 1,
          explain: "I2C open-drain necesita pull-ups. Sin ellos: lecturas fantasma o NACK.",
        },
        chatSkill: "@skill:wiring",
        chatFocus: "Ayúdame a validar el bus I2C del gabinete (SDA/SCL/pull-ups).",
        artifactTab: "wiring",
        practice: "wiring",
      },
      {
        id: "elec-safe",
        title: "Cableado seguro 5V–24V",
        detail: "Fusible, GND estrella y conformal anti-salitre.",
        level: "especialidad",
        requires: ["elec-bus"],
        lesson: `Fusible en el positivo cerca de la fuente. GND en estrella al Buck — evita loops que meten ruido al ADC.
Conformal coating en PCB si el gabinete ve humedad/salitre (aún IP67: condensación interna ocurre).
Marca el dispositivo en Monitor solo cuando USB/serie responde de verdad.`,
        quiz: {
          question: "Antes de marcar un ESP32 «online» en Monitor, ¿qué evidencia mínima quieres?",
          options: [
            "Solo el título del proyecto",
            "Que el puerto/serie o telemetría responda de verdad",
            "Haber generado un STL",
            "Tener XP de SkillFall",
          ],
          correctIndex: 1,
          explain: "Monitor = vivo de verdad. Marcar online sin respuesta engaña al hub y al agente.",
        },
        chatSkill: "@skill:wiring",
        chatFocus: "Checklist de seguridad eléctrica del wiring y cuándo marcar Monitor.",
        artifactTab: "wiring",
        practice: "monitor",
      },
    ],
  },
  {
    id: "energy",
    title: "Energía & Resiliencia PR",
    blurb: "Solar, LiFePO4, LUMA, ASCE viento — lo que se usa hoy en la isla.",
    projectHints: ["solar", "lifepo4", "luma", "bms", "bater", "cisterna", "asce"],
    chatSkill: "@skill:asce7",
    safetyNote: "DC: polaridad, fusible, BMS intacto. Estructura: anclaje Cat 5 / 170+ mph.",
    modules: [
      {
        id: "energy-bms",
        title: "BMS y fusibles DC",
        detail: "Corriente de falla, polaridad y protección de celdas.",
        level: "fundamentos",
        lesson: `LiFePO4 + BMS: el BMS corta por over/under voltage y a menudo por overcurrent. No lo puentes «para probar».
Fusible dimensionado a cable y carga — más cerca del banco, mejor.
Polaridad invertida en DC destroza convertidores: marca rojo/negro y usa conectores keyed.`,
        quiz: {
          question: "Puenteas el BMS «solo un minuto» para probar un inversor. ¿Riesgo principal?",
          options: [
            "Ninguno en LiFePO4",
            "Sin protección de celda/corriente — daño o incendio",
            "Solo baja el WiFi",
            "Mejora el FoS",
          ],
          correctIndex: 1,
          explain: "El BMS es la red de seguridad del banco. Sin él, una celda o un corto no tienen árbitro.",
        },
        chatSkill: "@skill:physics",
        chatFocus: "BMS, fusibles DC y polaridad para un banco LiFePO4 en PR.",
        artifactTab: "resumen",
        practice: "steps",
      },
      {
        id: "energy-loads",
        title: "Carga crítica vs no crítica",
        detail: "Qué sobrevive un apagón LUMA.",
        level: "aplicado",
        requires: ["energy-bms"],
        lesson: `Apagón: prioriza comms, frío de medicinas/comida, bombeo esencial, iluminación mínima.
Corta AC no crítico, talleres 240V y cargas de confort. Autonomía = Wh_banco × DoD_seguro / W_críticos.
Documenta en bitácora qué cortaste y cuántas horas estimaste.`,
        quiz: {
          question: "Con banco limitado en apagón LUMA, priorizas…",
          options: [
            "Todo al 100%",
            "Críticas y cortar no críticas",
            "Solo el AC",
            "Desconectar el BMS primero",
          ],
          correctIndex: 1,
          explain: "Resiliencia = triaje de cargas. El BMS se queda; lo que se va son cargas no críticas.",
        },
        chatSkill: "@skill:bom",
        chatFocus: "Ayúdame a clasificar cargas críticas vs no críticas para mi setup PR.",
        artifactTab: "bitacora",
        practice: "steps",
      },
      {
        id: "energy-wind",
        title: "Anclaje 175 mph",
        detail: "ASCE 7 Cat 5: anclajes y hardware anti-salitre.",
        level: "especialidad",
        requires: ["energy-loads"],
        lesson: `ASCE 7 en PR costero: diseña para vientos Cat 5 (170+ mph). Paneles y gabinetes exteriores necesitan anclaje a estructura, no solo a lamina floja.
Tornillería Inox/galvanizada; evita madera no tratada en exposición.
Si es obra civil (casa), bloques rellenos + varilla — no improvises con straps de TV.`,
        quiz: {
          question: "Anclar un gabinete/panel exterior en costa PR implica sobre todo…",
          options: [
            "Cinta doble cara",
            "Anclaje estructural + hardware anti-salitre (ASCE 7)",
            "Más infill en el PETG",
            "Subir el baud a 921600",
          ],
          correctIndex: 1,
          explain: "Viento Cat 5 + salitre: sujeción a estructura real y metales que no se comen.",
        },
        chatSkill: "@skill:asce7",
        chatFocus: "Checklist ASCE 7 / anclaje 175 mph para mi instalación.",
        artifactTab: "resumen",
        practice: "steps",
      },
    ],
  },
  {
    id: "ai",
    title: "Ingeniería con AI 2026",
    blurb: "Claude · Gemini Argon · GPT Astra — brief, BOM, review; tú decides.",
    projectHints: [],
    chatSkill: "@skill:cad",
    modules: [
      {
        id: "ai-brief",
        title: "Brief a CAD externo",
        detail: "El chat orienta; Fusion/FreeCAD/Blender hacen el sólido.",
        level: "fundamentos",
        lesson: `Un buen brief: cotas, material, holguras, cargas, restricciones PR (salitre, IP, voltaje).
El modelo en chat es didáctico hasta que exportas/importas. No trates un STL genérico como plano de producción.
Herramientas: Plan mode para el enfoque; Chat para ejecutar con approvals.`,
        quiz: {
          question: "Antes de mecanizar/imprimir en serio, el asset 3D debería estar…",
          options: [
            "Solo en un screenshot del chat",
            "Importado o aprobado tras review (no solo gen didáctica)",
            "Con XP alto",
            "En modo Ask eternamente",
          ],
          correctIndex: 1,
          explain: "Import/aprobado > didáctico. La gen AI orienta; el sólido revisado fabrica.",
        },
        chatSkill: "@skill:cad",
        chatFocus: "Redacta un brief CAD listo para Fusion/FreeCAD de mi proyecto.",
        artifactTab: "despiece",
        practice: "despiece",
      },
      {
        id: "ai-validate",
        title: "Validar generación 3D",
        detail: "didáctico → importado → aprobado.",
        level: "aplicado",
        requires: ["ai-brief"],
        lesson: `Precedencia: import (gold) > assets curados > generación.
Estados: didáctico (aprender) → importado (viene de CAD real) → aprobado (listo a prototipo).
Revisa paredes mínimas, interferencias, acceso a tornillos y prensaestopas antes de aprobar.`,
        quiz: {
          question: "Orden correcto del asset 3D en Prometheus:",
          options: [
            "Gen AI siempre gana al import",
            "Import > assets curados > gen didáctica hasta aprobar",
            "Solo STL sin estados",
            "XP decide",
          ],
          correctIndex: 1,
          explain: "Import real primero; gen es andamiaje hasta que apruebas.",
        },
        chatSkill: "@skill:cad",
        chatFocus: "Audita el estado 3D del proyecto: didáctico vs importado vs aprobado.",
        artifactTab: "despiece",
        practice: "despiece",
      },
      {
        id: "ai-check",
        title: "Checklist antes de la mesa",
        detail: "Plan → aprobar → Chat con OK de hardware/export.",
        level: "especialidad",
        requires: ["ai-validate"],
        lesson: `Flujo Cursor-like: Ask (entender) → Plan (checklist) → Ejecutar en Chat.
USB, Monitor y export piden confirmación en UI — el agente no debe asumirlos hechos.
Bitácora: anota qué aprobaste y qué quedó pendiente.`,
        quiz: {
          question: "Modo correcto si aún no sabes si cablear o solo entender el circuito:",
          options: ["Chat a ciegas flasheando", "Ask o Plan antes de mutar hardware", "Marcar Monitor online", "Borrar el proyecto"],
          correctIndex: 1,
          explain: "Ask/Plan reducen daño. Chat ejecuta cuando ya hay enfoque y OKs de hardware.",
        },
        chatSkill: "@skill:bom",
        chatFocus: "Arma el checklist Plan→Ejecutar de mi siguiente sesión de taller.",
        artifactTab: "bitacora",
        practice: "steps",
      },
    ],
  },
];

export type TrackEvidence = {
  trackId: UniversityTrackId;
  matchedProjects: { id: string; title: string; progress: number }[];
  stepsDone: number;
  stepsTotal: number;
  wiringDone: number;
  wiringTotal: number;
  onlineDevices: number;
  /** Track-level: all module quizzes passed */
  quizPassed: boolean;
  modulesUnderstood: number;
  modulesTotal: number;
  /** 0–100 composite from evidence (not XP points). */
  evidenceScore: number;
};

export type ModuleStatus = {
  unlocked: boolean;
  quizPassed: boolean;
  practiceMet: boolean;
  /** Strong = quiz + (practice met or no practice required) */
  complete: boolean;
};

function projectMatchesTrack(project: EngineeringProject, track: UniversityTrack): boolean {
  if (track.id === "ai") return true;
  if (track.id === "elec" && project.id === PILOT_WIRING_PROJECT_ID) return true;
  if (track.id === "mech" && project.id === PILOT_WIRING_PROJECT_ID) return true;
  const hay = `${project.title} ${project.discipline || ""} ${project.description || ""}`.toLowerCase();
  return track.projectHints.some((h) => hay.includes(h));
}

function countSteps(project: EngineeringProject): { done: number; total: number } {
  let done = 0;
  let total = 0;
  for (const phase of Object.values(project.phases || {})) {
    if (!phase?.steps) continue;
    for (const s of phase.steps) {
      total++;
      if (s.completed) done++;
    }
  }
  return { done, total };
}

function countWiring(project: EngineeringProject): { done: number; total: number } {
  const steps = project.wiring?.steps;
  if (!steps?.length) return { done: 0, total: 0 };
  return {
    done: steps.filter((s) => s.done).length,
    total: steps.length,
  };
}

/** Legacy track-level map (still read for migration). */
export function getQuizPassedMap(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(QUIZ_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, boolean>;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

export function getModuleQuizMap(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(MODULE_QUIZ_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, boolean>;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

export function setModuleQuizPassed(moduleId: string, passed: boolean) {
  const map = getModuleQuizMap();
  map[moduleId] = passed;
  try {
    localStorage.setItem(MODULE_QUIZ_KEY, JSON.stringify(map));
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new Event("prometheus-university-changed"));
}

/** @deprecated Prefer setModuleQuizPassed — kept for old UI calls */
export function setQuizPassed(trackId: UniversityTrackId, passed: boolean) {
  const map = getQuizPassedMap();
  map[trackId] = passed;
  try {
    localStorage.setItem(QUIZ_KEY, JSON.stringify(map));
  } catch {
    /* ignore */
  }
  const track = getTrackById(trackId);
  if (passed && track) {
    for (const m of track.modules) setModuleQuizPassed(m.id, true);
  }
  window.dispatchEvent(new Event("prometheus-university-changed"));
}

function practiceMetFor(
  practice: PracticeKind,
  projects: EngineeringProject[],
  onlineDevices: number
): boolean {
  if (!practice) return true;
  if (practice === "monitor") return onlineDevices > 0;
  if (practice === "wiring") {
    return projects.some((p) => (p.wiring?.steps || []).some((s) => s.done));
  }
  if (practice === "despiece") {
    return projects.some((p) => countSteps(p).done > 0 || (p.wiring?.steps || []).length > 0);
  }
  if (practice === "steps") {
    return projects.some((p) => countSteps(p).done > 0);
  }
  return true;
}

export function getModuleStatus(
  track: UniversityTrack,
  module: UniversityModule,
  evidence?: TrackEvidence
): ModuleStatus {
  const quizMap = getModuleQuizMap();
  const legacy = getQuizPassedMap()[track.id];
  const quizPassed = !!quizMap[module.id] || !!legacy;

  const projects = listProjects().filter((p) => projectMatchesTrack(p, track));
  const online =
    evidence?.onlineDevices ??
    listOnlineDevices().filter((d) => projects.some((p) => p.id === d.projectId)).length;
  const practiceMet = practiceMetFor(module.practice ?? null, projects, online);

  const reqs = module.requires || [];
  const unlocked =
    reqs.length === 0 ||
    reqs.every((id) => !!quizMap[id] || !!legacy);

  const complete = quizPassed && practiceMet;

  return { unlocked, quizPassed, practiceMet, complete };
}

export function computeTrackEvidence(track: UniversityTrack): TrackEvidence {
  const projects = listProjects().filter((p) => projectMatchesTrack(p, track));
  let stepsDone = 0;
  let stepsTotal = 0;
  let wiringDone = 0;
  let wiringTotal = 0;
  const matchedProjects = projects.slice(0, 5).map((p) => {
    const st = countSteps(p);
    const w = countWiring(p);
    stepsDone += st.done;
    stepsTotal += st.total;
    wiringDone += w.done;
    wiringTotal += w.total;
    const progress =
      st.total > 0
        ? Math.round((st.done / st.total) * 100)
        : w.total > 0
          ? Math.round((w.done / w.total) * 100)
          : 0;
    return { id: p.id, title: p.title, progress };
  });

  const onlineDevices = listOnlineDevices().filter((d) =>
    projects.some((p) => p.id === d.projectId)
  ).length;

  const modulesTotal = track.modules.length;
  let modulesUnderstood = 0;
  for (const m of track.modules) {
    if (getModuleStatus(track, m).quizPassed) modulesUnderstood++;
  }
  const quizPassed = modulesTotal > 0 && modulesUnderstood >= modulesTotal;

  let score = 0;
  if (stepsTotal > 0) score += Math.min(30, Math.round((stepsDone / stepsTotal) * 30));
  if (wiringTotal > 0) score += Math.min(25, Math.round((wiringDone / wiringTotal) * 25));
  if (onlineDevices > 0) score += 10;
  if (modulesTotal > 0) score += Math.round((modulesUnderstood / modulesTotal) * 35);
  if (matchedProjects.length > 0 && score < 5) score = 5;

  return {
    trackId: track.id,
    matchedProjects,
    stepsDone,
    stepsTotal,
    wiringDone,
    wiringTotal,
    onlineDevices,
    quizPassed,
    modulesUnderstood,
    modulesTotal,
    evidenceScore: Math.min(100, score),
  };
}

export function getTrackById(id: UniversityTrackId): UniversityTrack | undefined {
  return UNIVERSITY_TRACKS.find((t) => t.id === id);
}

export function getModuleById(
  track: UniversityTrack,
  moduleId: string
): UniversityModule | undefined {
  return track.modules.find((m) => m.id === moduleId);
}

export function modulesByLevel(track: UniversityTrack): Record<CurriculumLevel, UniversityModule[]> {
  const out: Record<CurriculumLevel, UniversityModule[]> = {
    fundamentos: [],
    aplicado: [],
    especialidad: [],
  };
  for (const m of track.modules) out[m.level].push(m);
  return out;
}

/** Best project to open for a track (highest step progress, else pilot). */
export function suggestProjectForTrack(track: UniversityTrack): EngineeringProject | null {
  const matched = listProjects().filter((p) => projectMatchesTrack(p, track));
  if (matched.length === 0) {
    return listProjects().find((p) => p.id === PILOT_WIRING_PROJECT_ID) ?? listProjects()[0] ?? null;
  }
  return (
    [...matched].sort((a, b) => {
      const pa = countSteps(a);
      const pb = countSteps(b);
      const ra = pa.total ? pa.done / pa.total : 0;
      const rb = pb.total ? pb.done / pb.total : 0;
      return rb - ra;
    })[0] ?? null
  );
}

export function practiceLabel(kind: PracticeKind): string {
  switch (kind) {
    case "wiring":
      return "Marca ≥1 paso de wiring en Artifact";
    case "despiece":
      return "Abre despiece / avanza un paso del proyecto";
    case "steps":
      return "Completa un paso del proyecto";
    case "monitor":
      return "Marca dispositivo online en Monitor (con OK)";
    default:
      return "";
  }
}
